import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';

import { normalizeEmail } from '../../utils/normalize-email';

export interface SignInEmailDialogData {
  partyName: string;
  /** 'timesheets' | 'timecards' — only used in the wording. */
  feature: string;
}

/**
 * Asks for the HCP's sign-in email when turning on timesheets or timecards for a
 * party that has none on file.
 *
 * Why this exists: NetSuite-imported parties are read-only in HC, and most of them
 * arrive with no email, so "add an email to the party" was impossible advice. The
 * email is stored on the SPEC (`principalEmail`) instead of the party — it's what
 * the HCP signs in to the mobile app with, what proxies find their work by, and
 * what the backend requires. The party record is never touched.
 *
 * Closes with the normalized email, or null on cancel.
 */
@Component({
  selector: 'app-sign-in-email-dialog',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MatDialogModule, MatButtonModule, MatFormFieldModule, MatInputModule, MatIconModule],
  template: `
    <h2 mat-dialog-title class="dialog-title"><mat-icon>alternate_email</mat-icon> Sign-in email needed</h2>
    <mat-dialog-content>
      <p class="dialog-message">
        <b>{{ data.partyName }}</b> has no email on file. Enter the email this person will use to sign in
        and submit {{ data.feature }}. It is saved on the {{ data.feature }} spec only — the party record
        is not changed.
      </p>
      <mat-form-field appearance="outline" class="full-width">
        <mat-label>Sign-in email</mat-label>
        <input matInput type="email" [formControl]="email" (keydown.enter)="save()" cdkFocusInitial>
        @if (email.hasError('email')) { <mat-error>Enter a valid email address.</mat-error> }
        @if (email.hasError('required')) { <mat-error>Email is required.</mat-error> }
      </mat-form-field>
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-stroked-button (click)="dialogRef.close(null)">Cancel</button>
      <button mat-raised-button color="primary" (click)="save()" [disabled]="email.invalid">Continue</button>
    </mat-dialog-actions>
  `,
  styles: [`
    .dialog-title { display: flex; align-items: center; gap: 8px; }
    .dialog-message { line-height: 1.6; color: #444; font-size: 14px; }
    .full-width { width: 100%; }
    mat-dialog-actions { padding: 12px 24px; }
    mat-dialog-actions button { margin-left: 8px; }
  `]
})
export class SignInEmailDialogComponent {
  dialogRef = inject(MatDialogRef<SignInEmailDialogComponent, string | null>);
  data: SignInEmailDialogData = inject(MAT_DIALOG_DATA);

  email = new FormControl('', [Validators.required, Validators.email]);

  save(): void {
    if (this.email.invalid) return;
    this.dialogRef.close(normalizeEmail(this.email.value));
  }
}
